export type ChatRole = 'user' | 'assistant';

export const THINKING_STEP_IDS = [
  'reading',
  'analyzing',
  'synthesizing'
] as const;

export type ThinkingStepId = (typeof THINKING_STEP_IDS)[number];

export const THINKING_STEP_STATES = ['active', 'done'] as const;

export type ThinkingStepState = (typeof THINKING_STEP_STATES)[number];

export interface ThinkingStep {
  id: ThinkingStepId;
  state: ThinkingStepState;
}

export interface DisplayedThinkingStep {
  id: ThinkingStepId;
  state: ThinkingStepState | 'pending';
}

export const BEKKI_SERVER_ERROR_CODES = [
  'generation_stopped',
  'rate_limited',
  'backend_unavailable',
  'tool_failed',
  'context_too_long',
  'internal'
] as const;

export type BekkiServerErrorCode = (typeof BEKKI_SERVER_ERROR_CODES)[number];

export type BekkiErrorCode = BekkiServerErrorCode | 'connection';

export interface ChatProvenance {
  dataAsOf: string;
  period?: { from: string; to: string };
  summary?: string;
  timeZone?: string;
}

export type ChatChartType = 'bar' | 'line' | 'pie';

export interface ChatChartSeries {
  name: string;
  values: number[];
}

export interface ChatChart {
  id: string;
  type: ChatChartType;
  title: string;
  categories: string[];
  series: ChatChartSeries[];
  unit?: string;
  stacked?: boolean;
}

export type ChatTableColumnType = 'text' | 'number';

export interface ChatTableColumn {
  label: string;
  type: ChatTableColumnType;
}

export type ChatTableCell = string | number | null;

export interface ChatTable {
  id: string;
  title: string;
  columns: ChatTableColumn[];
  rows: ChatTableCell[][];
}

export interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  charts?: ChatChart[];
  tables?: ChatTable[];
  provenance?: ChatProvenance;
  error?: BekkiErrorCode;
}

export interface ChatHistoryEntry {
  role: ChatRole;
  text: string;
}

export interface ChatContext {
  tenantId?: string;
  dcId?: number;
  externalDepotId?: string;
  subcontractorId?: string;
  timeZone?: string;
  locale?: string;
  page?: ChatPage;
}

export interface ChatPageEntities {
  tourId?: string;
  tourNumber?: string;
  date?: string;
  dateFrom?: string;
  dateTo?: string;
  parcelId?: string;
  jobId?: string;
  internalDepotId?: string;
}

export interface ChatPage {
  route: string;
  title?: string;
  entities?: ChatPageEntities;
}

export interface ChatRequest {
  conversationId: string;
  message: string;
  history: ChatHistoryEntry[];
  context: ChatContext;
}

export type ChatEvent =
  | { type: 'start'; messageId: string }
  | { type: 'step'; step: ThinkingStep }
  | { type: 'delta'; text: string }
  | { type: 'chart'; chart: ChatChart }
  | { type: 'table'; table: ChatTable }
  | { type: 'provenance'; provenance: ChatProvenance }
  | { type: 'done' }
  | { type: 'error'; code: BekkiErrorCode };

export interface BekkiChatApi {
  sendMessage: (
    request: ChatRequest,
    onEvent: (event: ChatEvent) => void
  ) => Promise<void>;
  abort: () => void;
}
